import React from 'react';
import { Tooltip } from '@ublend-npm/aulaui-next';
import { format, isFuture, isPast, parseISO } from 'date-fns';

import { AssignmentDetails, Provider } from './assignmentType';
import { ProviderLogo } from './ProviderLogo';
import {
  AssignmentPropertiesContainer,
  VisibilityOffIcon,
  GroupIcon,
  AnonymousIcon,
  AssignmentStatusLabel,
  AssignmentUpcomingLabel,
  AssignmentNotGradedStatusLabel,
  AssignmentGradedStatusLabel,
  AssignmentPastDueStatusLabel,
  AssignmentNotSubmittedStatusLabel,
} from './Assignment.styled';

const renderEducatorAssignmentStatus = (
  assignmentDetails: AssignmentDetails
) => {
  const {
    status: assignmentStatus,
    startDate,
    endDate,
    gradesReleasedAt,
    provider,
  } = assignmentDetails;

  if (startDate && isFuture(parseISO(startDate))) {
    return (
      <AssignmentUpcomingLabel>
        <span>Upcoming</span>
      </AssignmentUpcomingLabel>
    );
  }

  if (assignmentStatus === 'live') {
    if (endDate && isPast(parseISO(endDate))) {
      return (
        <AssignmentPastDueStatusLabel>
          <span>Past due date</span>
        </AssignmentPastDueStatusLabel>
      );
    }

    return (
      <AssignmentStatusLabel>
        <span>Open</span>
      </AssignmentStatusLabel>
    );
  }

  if (assignmentStatus === 'closed') {
    if (gradesReleasedAt && isPast(parseISO(gradesReleasedAt))) {
      return (
        <AssignmentGradedStatusLabel>
          <span>Grades released</span>
        </AssignmentGradedStatusLabel>
      );
    }

    if (provider !== Provider.Quizzes)
      return (
        <AssignmentNotGradedStatusLabel>
          <span>Grades not released</span>
        </AssignmentNotGradedStatusLabel>
      );
  }

  return null;
};

const renderStudentAssignmentStatus = (
  assignmentDetails: AssignmentDetails
) => {
  const {
    status: assignmentStatus,
    startDate,
    endDate,
    gradesReleasedAt,
    submission,
  } = assignmentDetails;

  if (startDate && isFuture(parseISO(startDate))) {
    return (
      <AssignmentUpcomingLabel>
        <span>Upcoming</span>
      </AssignmentUpcomingLabel>
    );
  }

  if (assignmentStatus === 'live') {
    if (endDate && isPast(parseISO(endDate)) && !submission?.submittedAt) {
      return (
        <AssignmentPastDueStatusLabel>
          <span>Past due date</span>
        </AssignmentPastDueStatusLabel>
      );
    }

    // for students; the assignment details endpoint returns
    // an empty object for submission when there are no submissions
    if (!submission?.submittedAt) {
      return (
        <AssignmentNotSubmittedStatusLabel>
          <span>Not submitted</span>
        </AssignmentNotSubmittedStatusLabel>
      );
    }

    return (
      <AssignmentStatusLabel>
        <span>
          Submitted:{' '}
          {format(new Date(submission.submittedAt), 'MMM do, y, HH:mm')}
        </span>
      </AssignmentStatusLabel>
    );
  }

  const gradesNotReleased =
    !gradesReleasedAt ||
    (gradesReleasedAt && isFuture(parseISO(gradesReleasedAt)));
  if (gradesNotReleased) {
    return (
      <AssignmentNotGradedStatusLabel>
        <span>Grades not released</span>
      </AssignmentNotGradedStatusLabel>
    );
  }

  if (submission?.displayGrade) {
    return (
      <AssignmentGradedStatusLabel>
        <span>Grade: {submission.displayGrade}</span>
      </AssignmentGradedStatusLabel>
    );
  }

  return null;
};

const tooltipTitles = {
  hidden: 'Assignment description is hidden from students.',
  group: 'This is a group assignment',
  anonymous: 'This assignment is anonymous',
};

export const AssignmentProperties = ({
  assignmentDetails,
  isEducator,
}: {
  assignmentDetails: AssignmentDetails;
  isEducator: boolean;
}) => (
  <AssignmentPropertiesContainer>
    <ProviderLogo provider={assignmentDetails.provider} />
    {isEducator
      ? renderEducatorAssignmentStatus(assignmentDetails)
      : renderStudentAssignmentStatus(assignmentDetails)}
    {isEducator && assignmentDetails.isHidden && (
      <Tooltip title={tooltipTitles.hidden}>
        <VisibilityOffIcon data-testid="VisibilityOffIcon" />
      </Tooltip>
    )}
    {assignmentDetails.isAnonymised && (
      <Tooltip title={tooltipTitles.anonymous}>
        <AnonymousIcon />
      </Tooltip>
    )}
    {assignmentDetails.groupSet && (
      <Tooltip title={tooltipTitles.group}>
        <GroupIcon data-testid="GroupIcon" />
      </Tooltip>
    )}
  </AssignmentPropertiesContainer>
);
